import { forwardRef, useCallback, useEffect } from "react";
import {
  Link as RouterLink,
  type LinkProps,
  useLocation,
  useNavigate,
  type NavigateOptions,
} from "react-router-dom";

export type Lang = "fr" | "en";

export const SITE_URL = "https://nettoyageboudreau.com";

/** Returns the active language, derived from the "/en" URL prefix. */
export const langFromPath = (pathname: string): Lang =>
  pathname === "/en" || pathname.startsWith("/en/") ? "en" : "fr";

/** Removes the "/en" prefix from a pathname, always returning a FR path. */
export const stripLang = (pathname: string): string => {
  if (pathname === "/en" || pathname === "/en/") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname || "/";
};

/** Prefixes a FR path with "/en" when the target language is English. */
export const localizePath = (path: string, lang: Lang): string => {
  if (lang !== "en") return path;
  if (!path.startsWith("/")) return path; // hashes, tel:, mailto:, external
  if (path === "/en" || path.startsWith("/en/")) return path;
  return path === "/" ? "/en" : `/en${path}`;
};

export const useLang = (): Lang => langFromPath(useLocation().pathname);

/** t("texte français", "english text") */
export const useT = () => {
  const lang = useLang();
  return useCallback(
    (fr: string, en?: string) => (lang === "en" && en ? en : fr),
    [lang],
  );
};

/** Returns a function that prefixes internal paths with the active language. */
export const usePath = () => {
  const lang = useLang();
  return useCallback((path: string) => localizePath(path, lang), [lang]);
};

/** Drop-in replacement for react-router's Link that keeps the active language. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, ...props }, ref) => {
    const lang = useLang();
    const localized = typeof to === "string" ? localizePath(to, lang) : to;
    return <RouterLink ref={ref} to={localized} {...props} />;
  },
);
Link.displayName = "LocalizedLink";

/** navigate() that keeps the active language prefix. */
export const useLocalizedNavigate = () => {
  const navigate = useNavigate();
  const lang = useLang();
  return useCallback(
    (to: string, options?: NavigateOptions) =>
      navigate(localizePath(to, lang), options),
    [navigate, lang],
  );
};

/**
 * Sets <html lang>, hreflang alternates and x-default for every route so that
 * Google indexes the FR and EN versions of each page separately.
 */
export const LangHead = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const lang = langFromPath(pathname);
    const frPath = stripLang(pathname);
    const enPath = localizePath(frPath, "en");

    document.documentElement.lang = lang === "en" ? "en-CA" : "fr-CA";

    // Drop the static fallback alternates from index.html so a page never
    // exposes two conflicting sets of hreflang links.
    document
      .querySelectorAll('link[rel="alternate"][hreflang]:not([data-hreflang])')
      .forEach((el) => el.remove());

    const alternates: Array<[string, string]> = [
      ["fr-CA", `${SITE_URL}${frPath}`],
      ["en-CA", `${SITE_URL}${enPath}`],
      ["x-default", `${SITE_URL}${frPath}`],
    ];

    const links = alternates.map(([hreflang, href]) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      el.setAttribute("data-hreflang", "true");
      document.head.appendChild(el);
      return el;
    });

    return () => links.forEach((el) => el.remove());
  }, [pathname]);

  return null;
};